import axios, {
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios'
import * as Sentry from '@sentry/react'
import { API_CONFIG, ENDPOINTS } from '@/config/api.config'
import type { ApiError } from '@/types'

const api = axios.create({
  baseURL: API_CONFIG.baseURL,
  timeout: API_CONFIG.timeout,
  headers: API_CONFIG.headers,
  withCredentials: true,
})

// Request interceptor - Add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Silent refresh state
let isRefreshing = false
let refreshPromise: Promise<string | null> | null = null

function doRefresh(): Promise<string | null> {
  return axios
    .post<{ token: string }>(
      `${API_CONFIG.baseURL}${ENDPOINTS.auth.refreshToken}`,
      {},
      { withCredentials: true }
    )
    .then((res) => {
      const newToken = res.data.token
      localStorage.setItem('token', newToken)
      return newToken
    })
    .catch(() => {
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      return null
    })
}

// Response interceptor - Handle errors globally
api.interceptors.response.use(
  (response: AxiosResponse) => {
    return response
  },
  async (error: AxiosError<ApiError>) => {
    const status = error.response?.status || 500
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }

    Sentry.addBreadcrumb({
      category: 'api',
      message: `${error.config?.method?.toUpperCase()} ${error.config?.url} → ${status}`,
      level: status >= 500 ? 'error' : 'warning',
      data: {
        url: error.config?.url,
        method: error.config?.method,
        status,
      },
    })

    if (status >= 500) {
      Sentry.captureException(error)
    }

    if (
      status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !originalRequest.url?.includes(ENDPOINTS.auth.refreshToken)
    ) {
      originalRequest._retry = true

      if (!isRefreshing) {
        isRefreshing = true
        refreshPromise = doRefresh().finally(() => {
          isRefreshing = false
          refreshPromise = null
        })
      }

      const newToken = await refreshPromise
      if (newToken) {
        originalRequest.headers.Authorization = `Bearer ${newToken}`
        return api(originalRequest)
      }

      Sentry.setUser(null)
      window.location.href = '/auth'
      return Promise.reject(error)
    }

    if (status === 401) {
      Sentry.setUser(null)
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      window.location.href = '/auth'
    }

    const apiError: ApiError = {
      statusCode: status,
      error: error.response?.data?.error || 'Error',
      message: error.response?.data?.message || 'Erro ao processar requisição',
      code: error.response?.data?.code,
      details: error.response?.data?.details,
    }

    return Promise.reject(apiError)
  }
)

// Helper functions
export const apiClient = {
  get: <T>(url: string, config?: AxiosRequestConfig) =>
    api.get<T>(url, config).then((res) => res.data),

  post: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    api.post<T>(url, data, config).then((res) => res.data),

  put: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    api.put<T>(url, data, config).then((res) => res.data),

  patch: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    api.patch<T>(url, data, config).then((res) => res.data),

  delete: <T>(url: string, config?: AxiosRequestConfig) =>
    api.delete<T>(url, config).then((res) => res.data),
}

export default api
